/**
 * SDK 滑块验证码组件(从 @/components/captcha/LazySlideCaptcha 搬入)
 *
 * 纯 React + 内联 SVG,无 npm 依赖,可直接进 SDK bundle。原 LazySlideCaptcha.css
 * 转字符串并入 SDK_STYLE(src/sdk/lib/styles.ts),shadow DOM 隔离类名保留 lazy-slide-captcha-*。
 * 类型从 @/types/captcha 引(纯类型,esbuild 擦除,不进 bundle)。
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
} from 'react';
import type {
  LazySlideCaptchaProps,
  LazySlideCaptchaRef,
  SlideCaptchaTrackPoint,
} from '@/types/captcha';

type DragMoveEvent = MouseEvent | TouchEvent;
type DragStartEvent = ReactMouseEvent<HTMLButtonElement> | ReactTouchEvent<HTMLButtonElement>;
type PointEvent = DragMoveEvent | DragStartEvent;

type CaptchaStyle = CSSProperties & {
  '--captcha-width': string;
  '--captcha-pwidth': string;
};

interface IconSizeProps {
  width: number;
  height: number;
}

const getPoint = (event: PointEvent) => {
  if ('touches' in event) {
    const source = event.touches[0] ?? event.changedTouches[0];

    return {
      x: source?.pageX ?? 0,
      y: source?.pageY ?? 0,
    };
  }

  return {
    x: event.pageX,
    y: event.pageY,
  };
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const LazySlideCaptcha = forwardRef<LazySlideCaptchaRef, LazySlideCaptchaProps>(
  function LazySlideCaptcha(
    {
      width = 340,
      height = 212,
      barHeight = 40,
      handlerIconWidth = 16,
      handlerIconHeight = 16,
      background = '#eee',
      circle = false,
      radius = '4px',
      text = '按住滑块拖动',
      progressBarBg = '#76c61d',
      successTip = '验证通过，超过80%用户',
      failTip = '验证未通过，拖动滑块将悬浮图像正确合并',
      showRefresh = false,
      onFinish,
      onRefresh,
    },
    ref
  ) {
    const backgroundRef = useRef<HTMLImageElement | null>(null);
    const sliderRef = useRef<HTMLImageElement | null>(null);
    const startPointRef = useRef({ x: 0, y: 0 });
    const startSlidingTimeRef = useRef<Date | null>(null);
    const tracksRef = useRef<SlideCaptchaTrackPoint[]>([]);
    const draggingRef = useRef(false);
    const passingRef = useRef(false);
    const finishRef = useRef(false);
    const lastTouchTimeRef = useRef(0);
    const handleDragMovingRef = useRef<((event: DragMoveEvent) => void) | null>(null);
    const handleDragFinishRef = useRef<((event: DragMoveEvent) => void) | null>(null);

    const [position, setPosition] = useState(0);
    const [isPassing, setIsPassing] = useState(false);
    const [isFinish, setIsFinish] = useState(false);
    const [showVerifyTip, setShowVerifyTip] = useState(false);
    const [showVerifyLoading, setShowVerifyLoading] = useState(false);
    const [showGenerateLoading, setShowGenerateLoading] = useState(false);
    const [src, setSrc] = useState('');
    const [sliderSrc, setSliderSrc] = useState('');

    const reset = useCallback(() => {
      draggingRef.current = false;
      finishRef.current = false;
      passingRef.current = false;
      tracksRef.current = [];
      startSlidingTimeRef.current = null;
      setPosition(0);
      setIsFinish(false);
      setIsPassing(false);
      setShowGenerateLoading(false);
      setShowVerifyLoading(false);
      setShowVerifyTip(false);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        startRequestGenerate() {
          reset();
          setShowGenerateLoading(true);
        },
        endRequestGenerate(backgroundImage, sliderImage) {
          setShowGenerateLoading(false);
          setSrc(backgroundImage || '');
          setSliderSrc(sliderImage || '');
        },
        startRequestVerify() {
          setShowVerifyLoading(true);
        },
        endRequestVerify(passing) {
          const nextPassing = Boolean(passing);
          passingRef.current = nextPassing;
          setIsPassing(nextPassing);
          setShowVerifyLoading(false);
          setShowVerifyTip(true);
        },
        reset,
      }),
      [reset]
    );

    const handleWindowDragMoving = useCallback((event: DragMoveEvent) => {
      handleDragMovingRef.current?.(event);
    }, []);

    const handleWindowDragFinish = useCallback((event: DragMoveEvent) => {
      handleDragFinishRef.current?.(event);
    }, []);

    const removeListeners = useCallback(() => {
      window.removeEventListener('touchmove', handleWindowDragMoving);
      window.removeEventListener('touchend', handleWindowDragFinish);
      window.removeEventListener('mousemove', handleWindowDragMoving);
      window.removeEventListener('mouseup', handleWindowDragFinish);
    }, [handleWindowDragFinish, handleWindowDragMoving]);

    const handleDragStart = useCallback(
      (event: DragStartEvent) => {
        if (event.type === 'touchstart') {
          lastTouchTimeRef.current = Date.now();
        } else if (Date.now() - lastTouchTimeRef.current < 500) {
          return;
        }

        if (passingRef.current || !src || !sliderSrc || finishRef.current) return;

        event.preventDefault();
        const point = getPoint(event);
        startPointRef.current = point;
        startSlidingTimeRef.current = new Date();
        tracksRef.current = [];
        draggingRef.current = true;

        window.addEventListener('touchmove', handleWindowDragMoving, { passive: false });
        window.addEventListener('touchend', handleWindowDragFinish);
        window.addEventListener('mousemove', handleWindowDragMoving);
        window.addEventListener('mouseup', handleWindowDragFinish);
      },
      [handleWindowDragFinish, handleWindowDragMoving, sliderSrc, src]
    );

    const handleDragMoving = useCallback(
      (event: DragMoveEvent) => {
        if (!draggingRef.current || passingRef.current || !src || !sliderSrc || finishRef.current) {
          return;
        }

        event.preventDefault();
        const point = getPoint(event);
        const maxLeft = width - barHeight;
        const x = clamp(point.x - startPointRef.current.x, 0, maxLeft);
        const y = point.y - startPointRef.current.y;
        const startTime = startSlidingTimeRef.current;

        setPosition(x);
        tracksRef.current.push({
          x: Math.round(x),
          y: Math.round(y),
          t: startTime ? new Date().getTime() - startTime.getTime() : 0,
        });
      },
      [barHeight, sliderSrc, src, width]
    );

    const handleDragFinish = useCallback(() => {
      if (!draggingRef.current || passingRef.current || !src || !sliderSrc || finishRef.current) {
        return;
      }

      draggingRef.current = false;
      finishRef.current = true;
      setIsFinish(true);
      removeListeners();

      onFinish?.({
        backgroundImageWidth: backgroundRef.current?.offsetWidth || width,
        backgroundImageHeight: backgroundRef.current?.offsetHeight || height,
        sliderImageWidth: sliderRef.current?.offsetWidth || barHeight,
        sliderImageHeight: sliderRef.current?.offsetHeight || height,
        startTime: startSlidingTimeRef.current,
        endTime: new Date(),
        tracks: tracksRef.current,
      });
    }, [barHeight, height, onFinish, removeListeners, sliderSrc, src, width]);

    useEffect(() => {
      handleDragMovingRef.current = handleDragMoving;
      handleDragFinishRef.current = handleDragFinish;
    }, [handleDragFinish, handleDragMoving]);

    useEffect(() => removeListeners, [removeListeners]);

    const barRadius = circle ? `${barHeight / 2}px` : radius;
    const progressRadius = circle ? `${barHeight / 2}px 0 0 ${barHeight / 2}px` : radius;
    const rootStyle: CaptchaStyle = {
      '--captcha-width': `${Math.floor(width / 2)}px`,
      '--captcha-pwidth': `${-Math.floor(width / 2)}px`,
    };

    return (
      <div className="lazy-slide-captcha" style={rootStyle}>
        <div className="lazy-slide-captcha__main" style={{ width, height }}>
          {src ? (
            <img
              ref={backgroundRef}
              src={src}
              className="lazy-slide-captcha__background"
              alt="background"
            />
          ) : null}
          {sliderSrc ? (
            <img
              ref={sliderRef}
              src={sliderSrc}
              className="lazy-slide-captcha__slider"
              alt="slider"
              style={{ left: position }}
            />
          ) : null}
          {showVerifyTip ? (
            <div className="lazy-slide-captcha__message">
              <div className="lazy-slide-captcha__message-icon">
                {isPassing ? <SuccessIcon /> : <FailIcon />}
              </div>
              <div className="lazy-slide-captcha__message-text">
                {isPassing ? successTip : failTip}
              </div>
            </div>
          ) : null}
          {showGenerateLoading ? (
            <div className="lazy-slide-captcha__message lazy-slide-captcha__message--loading">
              <div className="lazy-slide-captcha__loading-icon" />
              <div className="lazy-slide-captcha__message-text">加载中...</div>
            </div>
          ) : null}
          {showVerifyLoading ? (
            <div className="lazy-slide-captcha__message">
              <div className="lazy-slide-captcha__loading-icon" />
            </div>
          ) : null}
        </div>

        <div
          className="lazy-slide-captcha__bar"
          style={{
            width,
            height: barHeight,
            lineHeight: `${barHeight}px`,
            background,
            borderRadius: barRadius,
          }}
        >
          <div
            className="lazy-slide-captcha__progress"
            style={{
              width: isFinish && !isPassing ? position + barHeight / 2 : position + barHeight / 2,
              height: barHeight,
              background: progressBarBg,
              borderRadius: progressRadius,
            }}
          />
          <div className="lazy-slide-captcha__bar-text" style={{ height: barHeight, width }}>
            {text}
          </div>
          <button
            type="button"
            className="lazy-slide-captcha__handler"
            style={{
              left: position,
              width: barHeight,
              height: barHeight - 2,
            }}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            aria-label={text}
          >
            <ArrowIcon width={handlerIconWidth} height={handlerIconHeight} />
          </button>
        </div>

        {showRefresh ? (
          <div className="lazy-slide-captcha__actions">
            <button
              type="button"
              className="lazy-slide-captcha__refresh"
              onClick={() => {
                reset();
                onRefresh?.();
              }}
            >
              <RefreshIcon />
              <span>刷新</span>
            </button>
          </div>
        ) : null}
      </div>
    );
  }
);

function SuccessIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <g
        stroke="#fff"
        strokeWidth="1.5"
        fill="none"
        fillRule="evenodd"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22.776 4.073A13.2 13.2 0 0 0 14 .75C6.682.75.75 6.682.75 14S6.682 27.25 14 27.25 27.25 21.318 27.25 14c0-.284-.009-.566-.027-.845" />
        <path d="M7 12.5l7 7 13-13" />
      </g>
    </svg>
  );
}

function FailIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <g stroke="#fff" strokeWidth="1.5" fill="none" fillRule="evenodd">
        <circle cx="14" cy="14" r="13.25" />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.75 8.75l10.5 10.5M19.25 8.75l-10.5 10.5"
        />
      </g>
    </svg>
  );
}

function ArrowIcon({ width, height }: IconSizeProps) {
  return (
    <svg style={{ width, height }} viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M500.864 545.728a47.744 47.744 0 0 0 6.72-48.896 24.704 24.704 0 0 0-4.48-8.384L240.256 193.088a34.24 34.24 0 0 0-28.608-17.408 34.24 34.24 0 0 0-25.856 12.864 46.592 46.592 0 0 0 0 59.52l238.08 264.512-238.08 264.512a46.592 46.592 0 0 0-1.088 59.52 32 32 0 0 0 50.56 0l265.6-290.88z" />
      <path d="M523.84 248.064l236.992 264.512-238.08 264.512a46.592 46.592 0 0 0 0 59.52 32 32 0 0 0 50.56 0l265.6-292.608a47.744 47.744 0 0 0 6.72-48.832 24.704 24.704 0 0 0-4.48-8.448L578.304 191.36a34.24 34.24 0 0 0-55.552-2.816 46.592 46.592 0 0 0 1.088 59.52z" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg
      fill="#505050"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10,4 C12.0559549,4 13.9131832,5.04358655 15.0015086,6.68322231 L15,5.5 C15,5.22385763 15.2238576,5 15.5,5 C15.7761424,5 16,5.22385763 16,5.5 L16,8.5 C16,8.77614237 15.7761424,9 15.5,9 L12.5,9 C12.2238576,9 12,8.77614237 12,8.5 C12,8.22385763 12.2238576,8 12.5,8 L14.5842317,8.00000341 C13.7999308,6.20218044 12.0143541,5 10,5 C7.23857625,5 5,7.23857625 5,10 C5,12.7614237 7.23857625,15 10,15 C11.749756,15 13.3431487,14.0944653 14.2500463,12.6352662 C14.3958113,12.4007302 14.7041063,12.328767 14.9386423,12.4745321 C15.1731784,12.6202971 15.2451415,12.9285921 15.0993765,13.1631281 C14.0118542,14.9129524 12.0990688,16 10,16 C6.6862915,16 4,13.3137085 4,10 C4,6.6862915 6.6862915,4 10,4 Z"
        fillRule="nonzero"
      />
    </svg>
  );
}

export default LazySlideCaptcha;
